/**
 * @fileoverview Shared sub-components used by the ParcelDataPanel. These are
 *   presentation-only components that render from the unified data shape —
 *   no data access, no API calls, no Dexie reads.
 * @module ui-admin/parcel-data-panel/sub-components
 *
 * Responsibilities:
 * - Provide reusable UI primitives: PanelSection, ReadField, DataQualityBars,
 *   StatBox, ImportHistoryTable, TokenEntitlementEditor.
 * - Format dates and file sizes consistently across both hosts.
 *
 * Dependencies:
 * - lucide-react (icons)
 * - ./types (shared contracts)
 *
 * Architecture:
 * - Pure presentation components. All data comes from props.
 * - Theme-aware: accepts optional textPrimary/textSecondary/cardBorder/
 *   expandedBg style strings for system-web's themed panel. When absent,
 *   falls back to Tailwind classes (tenant-web's default theme).
 *
 * Notes:
 * - These components are extracted from system-web's DepartmentParcelDataTab
 *   and tenant-web's ParcelDataSettingsPage/ParcelDataSlideout, collapsing
 *   three separate copies of the same UI into one.
 */

import { useState, type ReactNode } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Info,
  Loader2,
  Save,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { DataQuality, ImportHistoryRow } from './types';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** Format an ISO date string as a localized date. */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Format a byte count as a human-readable file size. */
export function formatFileSize(bytes?: number | null): string {
  if (bytes == null || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let val = bytes;
  let unitIdx = 0;
  while (val >= 1024 && unitIdx < units.length - 1) {
    val /= 1024;
    unitIdx++;
  }
  return `${val.toFixed(val >= 100 ? 0 : 1)} ${units[unitIdx]}`;
}

// ---------------------------------------------------------------------------
// PanelSection — collapsible section with header badges + actions
// ---------------------------------------------------------------------------

/** A badge rendered inline in a section header. */
export interface HeaderBadge {
  label: string;
  /** Tailwind color scheme: green = positive, amber = warning, blue = info, gray = neutral, red = error */
  color?: 'green' | 'amber' | 'blue' | 'gray' | 'red';
  icon?: LucideIcon;
}

const BADGE_STYLES: Record<NonNullable<HeaderBadge['color']>, string> = {
  green: 'bg-green-50 text-green-700 border-green-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  gray: 'bg-gray-50 text-gray-600 border-gray-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

/**
 * @component PanelSection
 * Purpose:  Collapsible section with a header (icon + title + badges + actions).
 * Props:    title, icon, children, theme styles, defaultExpanded, headerActions, headerBadges.
 * Behavior: Click header to expand/collapse. Header actions stop propagation.
 * @reusable
 * Usage: Used by ParcelDataPanel for each section (config, source file, stats, history).
 * Rules: Presentation-only; no data access.
 */
export function PanelSection({
  title,
  icon: Icon,
  children,
  expandedBg,
  textSecondary,
  textPrimary,
  cardBorder,
  defaultExpanded = false,
  headerActions,
  headerBadges,
}: {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
  expandedBg?: string;
  textSecondary?: string;
  textPrimary?: string;
  cardBorder?: string;
  defaultExpanded?: boolean;
  headerActions?: ReactNode;
  headerBadges?: HeaderBadge[];
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="mb-3">
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none transition-colors hover:bg-gray-50/50"
        style={{
          background: expanded ? expandedBg : 'transparent',
          border: `1px solid ${cardBorder ?? '#e5e7eb'}`,
          borderRadius: expanded ? '8px 8px 0 0' : '8px',
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        <Chevron className="h-4 w-4 shrink-0" style={{ color: textSecondary }} />
        <Icon className="h-4 w-4 shrink-0" style={{ color: textSecondary }} />
        <h4 className="text-sm font-semibold flex-1" style={{ color: textPrimary }}>
          {title}
        </h4>
        {headerBadges && headerBadges.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {headerBadges.map((badge, i) => {
              const BadgeIcon = badge.icon;
              const colorCls = BADGE_STYLES[badge.color ?? 'gray'];
              return (
                <span
                  key={`${badge.label}-${i}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${colorCls}`}
                >
                  {BadgeIcon && <BadgeIcon className="h-3 w-3" />}
                  {badge.label}
                </span>
              );
            })}
          </div>
        )}
        {headerActions && (
          <div onClick={(e) => e.stopPropagation()}>
            {headerActions}
          </div>
        )}
      </div>
      {expanded && (
        <div
          className="rounded-b-lg p-3"
          style={{
            background: expandedBg,
            border: `1px solid ${cardBorder ?? '#e5e7eb'}`,
            borderTop: 'none',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReadField — label + value display
// ---------------------------------------------------------------------------

/**
 * @component ReadField
 * Purpose:  Render a label-value pair in a definition list layout.
 * Props:    label, value, mono, span2, theme colors.
 * @reusable
 * Usage: Used throughout the panel for read-only field display.
 * Rules: Presentation-only.
 */
export function ReadField({
  label,
  value,
  mono,
  span2,
  textPrimary,
  textSecondary,
}: {
  label: string;
  value?: string | number | null;
  mono?: boolean;
  span2?: boolean;
  textPrimary?: string;
  textSecondary?: string;
}) {
  const display = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <dt className="text-xs font-medium mb-0.5" style={{ color: textSecondary }}>{label}</dt>
      <dd className={`text-sm break-all ${mono ? 'font-mono text-xs' : ''}`} style={{ color: textPrimary }}>
        {display}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataQualityBars — visual quality percentage bars
// ---------------------------------------------------------------------------

/**
 * @component DataQualityBars
 * Purpose:  Render four data-quality coverage percentages as labeled bars,
 *           plus an optional "some metrics unavailable" notice.
 * Props:    stats (DataQuality), theme colors.
 * Behavior: Green ≥95%, amber ≥85%, red <85%, gray when null. When
 *           `stats.unavailableMetrics` is a non-empty array, an Info notice
 *           is rendered below the bars listing the metric names that could
 *           not be computed.
 * @reusable
 * Usage: Used for both file-level and import-level quality display.
 * Rules: Presentation-only.
 */
export function DataQualityBars({
  stats,
  textSecondary,
  textPrimary,
}: {
  stats: DataQuality;
  textSecondary?: string;
  textPrimary?: string;
}) {
  const bars = [
    { label: 'Owner Name', value: stats.ownerNamePercent },
    { label: 'Mailing Address', value: stats.mailingAddressPercent },
    { label: 'Situs Address', value: stats.situsAddressPercent },
    { label: 'Geometry Valid', value: stats.geometryValidPercent },
  ];
  const unavailable = stats.unavailableMetrics ?? [];
  return (
    <div className="space-y-2">
      {bars.map((bar) => {
        const v = bar.value;
        if (v == null) {
          return (
            <div key={bar.label} className="flex items-center gap-2">
              <div className="text-xs w-28 shrink-0" style={{ color: textSecondary }}>{bar.label}</div>
              <div className="flex-1 h-2 rounded-sm bg-gray-200" />
              <div className="text-xs w-10 text-right" style={{ color: textSecondary }}>—</div>
            </div>
          );
        }
        const cls = v >= 95 ? 'bg-green-500' : v >= 85 ? 'bg-amber-500' : 'bg-red-500';
        return (
          <div key={bar.label} className="flex items-center gap-2">
            <div className="text-xs w-28 shrink-0" style={{ color: textSecondary }}>{bar.label}</div>
            <div className="flex-1 h-2 rounded-sm bg-gray-200 overflow-hidden">
              <div className={`h-full rounded-sm ${cls}`} style={{ width: `${v}%` }} />
            </div>
            <div className="text-xs w-10 text-right tabular-nums" style={{ color: textPrimary }}>{v}%</div>
          </div>
        );
      })}
      {unavailable.length > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-gray-400 pt-1">
          <Info className="h-3.5 w-3.5" />
          Some metrics unavailable: {unavailable.join(', ')}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatBox — single statistic card
// ---------------------------------------------------------------------------

/**
 * @component StatBox
 * Purpose:  Render a single labeled statistic value in a card.
 * Props:    label, value, icon (optional).
 * @reusable
 * Usage: Used for import statistics and token display.
 * Rules: Presentation-only.
 */
export function StatBox({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | number;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-100 p-3 bg-white">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <div className="text-lg font-bold text-gray-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImportHistoryTable — per-department import history
// ---------------------------------------------------------------------------

/**
 * @component ImportHistoryTable
 * Purpose:  Render the department's import history as a table.
 * Props:    history (ImportHistoryRow[]), theme colors.
 * Behavior: Shows date, parcels, new, updates, duration, version per row.
 *           Empty state message when no history.
 * @reusable
 * Usage: Used in both system-admin and department-admin modes.
 * Rules: Presentation-only.
 */
export function ImportHistoryTable({
  history,
  textSecondary,
  textPrimary,
}: {
  history: ImportHistoryRow[];
  textSecondary?: string;
  textPrimary?: string;
}) {
  if (history.length === 0) {
    return (
      <p className="text-xs italic px-3 py-4 bg-gray-50 rounded-sm" style={{ color: textSecondary }}>
        No automated imports recorded for this department yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Date</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Parcels</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">New</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Updates</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Duration</th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Version</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {history.map((entry) => (
            <tr key={entry._id} className="hover:bg-gray-50/50">
              <td className="px-3 py-2 text-xs text-gray-600">{formatDate(entry.date)}</td>
              <td className="px-3 py-2 text-xs text-gray-700 tabular-nums">
                {entry.parcels?.toLocaleString() ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-gray-700 tabular-nums">
                {entry.newCount?.toLocaleString() ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-gray-700 tabular-nums">
                {entry.updatesCount?.toLocaleString() ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">{entry.duration ?? '—'}</td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {entry.version ? <code>{entry.version}</code> : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TokenEntitlementEditor — inline editor for tokensPerYear (system-admin only)
// ---------------------------------------------------------------------------

/**
 * @component TokenEntitlementEditor
 * Purpose:  Inline editor for the tokensPerYear entitlement (system-admin only).
 * Props:    tokensPerYear, onSave, onDone.
 * Behavior: Number input with save/cancel buttons.
 * @reusable
 * Usage: Used only in system-admin mode.
 * Rules: Presentation-only; save via callback.
 */
export function TokenEntitlementEditor({
  tokensPerYear,
  onSave,
  onDone,
}: {
  tokensPerYear: number;
  onSave: (tokensPerYear: number) => Promise<boolean>;
  onDone: () => void;
}) {
  const [value, setValue] = useState(tokensPerYear.toString());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 0) {
      setError('Enter a valid number (0 or greater)');
      return;
    }
    setSaving(true);
    setError(null);
    const ok = await onSave(parsed);
    setSaving(false);
    if (ok) onDone();
    else setError('Failed to save');
  };

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-16 text-sm border border-gray-300 rounded-sm px-2 py-1 focus:ring-blue-500 focus:border-blue-500"
        disabled={saving}
        aria-label="Tokens per year"
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
      </button>
      <button
        type="button"
        onClick={onDone}
        disabled={saving}
        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-60"
      >
        <X className="h-3 w-3" />
      </button>
      {error && <span className="text-xs text-red-600 ml-1">{error}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HazardLevelBadges — render hazard level chips
// ---------------------------------------------------------------------------

/**
 * @component HazardLevelBadges
 * Purpose:  Render hazard level chips for fire-hazard filter mode.
 * Props:    levels (string[]).
 * @reusable
 * Usage: Used in config, import stats, and source file sections.
 * Rules: Presentation-only.
 */
export function HazardLevelBadges({ levels }: { levels: string[] }) {
  if (levels.length === 0) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {levels.map((level) => (
        <span
          key={level}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-50 text-orange-700 rounded-full border border-orange-200 capitalize"
        >
          {level}
        </span>
      ))}
    </div>
  );
}
